import '../lib/dom-safety.web';
import '../../global.css';
// Workspace and panel motion (View Transitions); after global.css on purpose.
import '../../web-motion.css';
// Anchored pop-outs (WebPopover): header, scrolling body, footer.
import '../../web-popover.css';
// CV review and releases page (CvReviewScreen.web.tsx).
import '../../web-cv-review.css';
import '../i18n';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Stack, usePathname } from 'expo-router';
import Head from 'expo-router/head';
import { View, StyleSheet } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { QueryProvider } from '../providers/QueryProvider';
import { ThemeProvider } from '../providers/ThemeProvider';
import { AuthProvider } from '../providers/AuthProvider';
import { FontProvider, useFont } from '../providers/FontProvider';
import { DialogProvider, useDialog } from '../providers/DialogProvider';
import { AuthStepSheet } from '../components/auth/AuthStepSheet';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import {
  ensureValidAccessToken,
  startAppAuthBootstrap,
} from '../lib/app-auth';
import { getSocket, disconnectSocket } from '../lib/socket';
import { useMessageNotifications } from '../hooks/useMessageNotifications';
import { useNotificationHandler } from '../hooks/useNotificationHandler';
import { useUnreadCountSync } from '../hooks/useUnreadCountSync';
import { scheduleAfterFirstPaint } from '../lib/schedule-after-first-paint';
import { Toaster } from 'sonner';
import { WebAlertHost } from '../lib/alert.web';
import { StartupShell } from '../components/common/StartupShell';
import { ResumeImportProgress } from '../components/cv/ResumeImportComposer';
import { isPasswordRecoveryPath } from '../lib/auth-recovery-routes';
import { WebPageMetadata } from '../components/common/WebPageMetadata';
import { WebFontLinks } from '../components/common/WebFontLinks';
import { initializeSentry, wrapRootWithSentry } from '../lib/sentry';
import {
  identifyProductUser,
  resetProductUser,
  trackProductScreen,
} from '../lib/product-analytics';
import { syncPrivacyConsentToServer } from '../lib/privacy-consent-sync';
import {
  automationRunTargetFromLocation,
  savePostAuthTarget,
} from '../lib/auth-handoff';

import { ImpersonationBanner } from '../components/admin/ImpersonationBanner';
import { PrivacyPreferencesProvider } from '../providers/PrivacyPreferencesProvider';
import { PrivacyConsentSheet } from '../components/privacy/PrivacyConsentSheet';
import { usePrivacyPreferences } from '../hooks/usePrivacyPreferences';

startAppAuthBootstrap();

// The URL the page was opened with, captured before the router mounts: a
// signed-out visitor's guarded deep link (the Tasks result email's
// /auto-apply?runId=) is replaced by the login redirect, so it has to be
// remembered here to survive sign-in.
const INITIAL_WEB_LOCATION =
  typeof window !== 'undefined'
    ? { pathname: window.location.pathname, search: window.location.search }
    : null;

export const unstable_settings = {
  anchor: 'index',
};

// Skills handoff deliberately uses the protected shell once runtimeReady.
// Adding it here remounts the navigator on /skills and resets it to /cv.
const PROTECTED_SHELL_EXCLUDED_PATH_PREFIXES = [
  '/auth-bootstrap-check',
  '/password-bootstrap',
  '/resume-import',
  '/jobs-handoff',
  '/cv-share',
  '/cv-access',
  // The handoff claim chooser: a full-page decision like its cv-access /
  // cv-grant siblings, and it must render for signed-in users whose protected
  // runtime is not open yet (it stashes the token and hands to '/').
  '/cv-handoff',
  '/cv-grant',
  '/account-edit',
  '/subscription/return',
  '/admin-impersonation',
  // The browser extension connect handoff is a single approve-or-not decision
  // opened in its own tab. Wrapping it in the sidebar and agent tray buries that
  // decision inside the whole app and makes the tab look like somewhere to stay,
  // when the only correct next action is to approve and close it.
  '/extension',
  // `tabbio login` opens /cli/connect for the same single approve-or-cancel
  // decision; the next stop is the terminal, not the workspace.
  '/cli',
  // "Sign in with Tabbio": the partner's browser redirect lands here and leaves
  // again the moment the user decides. Same reasoning as /extension.
  '/oauth',
  // Password recovery stays a standalone page even for a signed-in visitor who
  // followed a mailed reset link: wrapping it in the sidebar would float the
  // form over the workspace they are supposedly locked out of.
  '/forgot-password',
  '/reset-password',
  // Live mode is a full-screen conversation with Nova, as on the phone. The
  // shell's floating sidebar button would sit on top of its close button.
  '/voice-handsfree',
];

const ProtectedAppProviders = lazy(
  () => import('../providers/ProtectedAppProviders.web'),
);
const ProtectedWebShell = lazy(async () => {
  const module = await import('../components/layout/ProtectedWebShell');
  return { default: module.ProtectedWebShell };
});

function shouldRenderProtectedShell(pathname: string | null) {
  if (!pathname) {
    return true;
  }

  return !PROTECTED_SHELL_EXCLUDED_PATH_PREFIXES.some((prefix) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function isResumeImportPath(pathname: string | null) {
  return pathname === '/resume-import' || pathname?.startsWith('/resume-import/');
}

function ResumeImportStartupShell() {
  const { theme } = useTheme();

  return (
    <View style={[styles.resumeImportFallback, { backgroundColor: theme.background }]}>
      <View style={[styles.resumeImportFallbackCard, { borderColor: theme.border }]}>
        <ResumeImportProgress isActive />
      </View>
    </View>
  );
}

function DialogRegistrar() {
  const { register } = useDialog();
  useEffect(() => {
    register('auth-step', AuthStepSheet);
  }, [register]);
  return null;
}

function readUserRole(user: unknown) {
  return typeof (user as { role?: unknown } | null)?.role === 'string'
    ? ((user as { role?: string | null }).role ?? null)
    : null;
}

function readUserLocale(user: unknown) {
  return typeof (user as { locale?: unknown } | null)?.locale === 'string'
    ? ((user as { locale?: string | null }).locale ?? null)
    : null;
}

function WebObservabilityTracker() {
  const pathname = usePathname();
  const {
    user,
    isAuthenticated,
    isOnboardingCompleted,
    bootstrapCheckFailed,
    needsPasswordBootstrap,
    impersonation,
  } = useAuth();
  const userRole = readUserRole(user);
  const userLocale = readUserLocale(user);
  const {
    analyticsEnabled,
    isHydrated: privacyHydrated,
    preferences: privacyPreferences,
  } = usePrivacyPreferences();

  // Server copy of the privacy decision, so the API can respect it for the
  // events it emits with no browser attached (webhooks, lifecycle hooks).
  // Deduplicated inside; a re-render costs nothing.
  useEffect(() => {
    if (!isAuthenticated || !user?.id || impersonation || !privacyHydrated) {
      return;
    }
    void syncPrivacyConsentToServer(user.id, privacyPreferences);
  }, [
    impersonation,
    isAuthenticated,
    privacyHydrated,
    privacyPreferences,
    user?.id,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || impersonation) {
      resetProductUser();
      return;
    }

    identifyProductUser({
      userId: user.id,
      role: userRole,
      locale: userLocale,
    });
  }, [
    analyticsEnabled,
    impersonation,
    isAuthenticated,
    user?.id,
    userLocale,
    userRole,
  ]);

  useEffect(() => {
    if (impersonation) return;
    trackProductScreen(pathname, {
      route: pathname,
      authState: isAuthenticated ? 'authenticated' : 'anonymous',
      onboardingCompleted: Boolean(isOnboardingCompleted),
      bootstrapCheckFailed: Boolean(bootstrapCheckFailed),
      needsPasswordBootstrap: Boolean(needsPasswordBootstrap),
    });
  }, [
    bootstrapCheckFailed,
    isAuthenticated,
    isOnboardingCompleted,
    needsPasswordBootstrap,
    impersonation,
    pathname,
    analyticsEnabled,
  ]);

  return null;
}

function WebSentryInitializer() {
  useEffect(() => {
    scheduleAfterFirstPaint(() => {
      initializeSentry();
    });
  }, []);

  return null;
}

function WebAuthEffects() {
  const { isAuthenticated, isTokenReady } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady) {
      disconnectSocket();
      return;
    }

    let isActive = true;

    void ensureValidAccessToken().then((token) => {
      if (!isActive) {
        return;
      }

      if (token) {
        const socket = getSocket();
        if (!socket.connected) {
          socket.connect();
        }
      } else {
        disconnectSocket();
      }
    });

    return () => {
      isActive = false;
      disconnectSocket();
    };
  }, [isAuthenticated, isTokenReady]);

  useMessageNotifications();
  useNotificationHandler();
  useUnreadCountSync();

  return null;
}

function DeferredWebAuthEffects() {
  const { isAuthenticated, isTokenReady, impersonation } = useAuth();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady || impersonation) {
      setEnabled(false);
      disconnectSocket();
      return;
    }

    return scheduleAfterFirstPaint(() => {
      setEnabled(true);
    });
  }, [impersonation, isAuthenticated, isTokenReady]);

  return enabled ? <WebAuthEffects /> : null;
}

function WebLayoutInner() {
  const {
    isAuthenticated,
    isTokenReady,
    isOnboardingCompleted,
    bootstrapCheckFailed,
    needsPasswordBootstrap,
    isAuthLoading,
  } = useAuth();
  const { theme } = useTheme();
  const { fontsLoaded } = useFont();
  const pathname = usePathname();
  const isBooting = isAuthLoading || !fontsLoaded;
  const isResumeImport = isResumeImportPath(pathname);

  // Once auth first settles: a signed-out visitor who opened a Tasks run link
  // keeps it as the post-auth target (index.tsx carries it into login as
  // ?next=). Written during render on purpose: the anchor index route reads
  // the stored target in this same render pass, before any effect runs. Only
  // the FIRST settled state counts, so signing out later never resurrects it.
  const initialAuthSettledRef = useRef(false);
  if (!isBooting && !initialAuthSettledRef.current) {
    initialAuthSettledRef.current = true;
    const runTarget =
      !isAuthenticated && INITIAL_WEB_LOCATION
        ? automationRunTargetFromLocation(INITIAL_WEB_LOCATION)
        : null;
    if (runTarget) {
      savePostAuthTarget(runTarget);
    }
  }
  const shouldLoadProtectedRuntime =
    isAuthenticated &&
    isTokenReady &&
    !bootstrapCheckFailed &&
    !needsPasswordBootstrap &&
    !!isOnboardingCompleted;
  const shouldShowProtectedShell =
    shouldLoadProtectedRuntime && shouldRenderProtectedShell(pathname);

  if (isBooting) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <WebPageMetadata />
        <StatusBar style="auto" />
        {isResumeImport ? <ResumeImportStartupShell /> : <StartupShell />}
      </View>
    );
  }

  const navigator = (
    <Stack
      screenOptions={{
        headerShown: false,
        contentStyle: {
          backgroundColor: theme.background,
        },
      }}
    >
      <Stack.Screen name="s" options={{ headerShown: false }} />
      <Stack.Screen name="cv-email" options={{ headerShown: false }} />
      <Stack.Screen name="auth-complete" options={{ headerShown: false }} />
      <Stack.Screen
        name="admin-impersonation"
        options={{ headerShown: false }}
      />
      <Stack.Screen name="uae-pass" options={{ headerShown: false }} />
      <Stack.Screen name="resume-import" options={{ headerShown: false }} />
      <Stack.Screen name="jobs-handoff" options={{ headerShown: false }} />
      <Stack.Screen name="skills-handoff" options={{ headerShown: false }} />
      {/* Unguarded on purpose: the consent screen does its own auth-aware work
          (it parks the authorize request and bounces to login), so putting it
          behind a guard would silently no-op the partner's redirect. */}
      <Stack.Screen name="oauth/authorize" options={{ headerShown: false }} />
      {/* Password recovery lives in (auth) but, unlike login and register, has
          to open for a signed-in visitor too: someone already signed in here
          who follows a mailed reset link would otherwise have the whole group
          unmounted under them and land on the workspace default, token lost. */}
      <Stack.Protected guard={!isAuthenticated || isPasswordRecoveryPath(pathname)}>
        <Stack.Screen name="(auth)" />
      </Stack.Protected>

      <Stack.Protected
        guard={
          isAuthenticated &&
          isTokenReady &&
          !bootstrapCheckFailed &&
          !needsPasswordBootstrap &&
          !isOnboardingCompleted
        }
      >
        <Stack.Screen name="(onboarding)" />
      </Stack.Protected>

      <Stack.Protected guard={isAuthenticated && isTokenReady}>
          <Stack.Screen name="support" options={{ headerShown: false }} />
        <Stack.Screen name="auth-bootstrap-check" />
        <Stack.Screen name="password-bootstrap" />
        <Stack.Screen name="account-edit" />
        <Stack.Screen
          name="subscription/return"
          options={{ headerShown: false }}
        />
      </Stack.Protected>

      <Stack.Protected guard={shouldLoadProtectedRuntime}>
        <Stack.Screen name="(app)" />
        <Stack.Screen name="(employer)" />
        <Stack.Screen name="messages" />
        <Stack.Screen name="notifications" />
        <Stack.Screen name="services" />
        <Stack.Screen name="settings" />
        <Stack.Screen
          name="upgrade"
          options={{
            presentation: 'transparentModal',
            animation: 'fade',
            headerShown: false,
            contentStyle: {
              backgroundColor: 'transparent',
            },
          }}
        />
        <Stack.Screen
          name="post-onboarding-welcome"
          options={{
            presentation: 'transparentModal',
            animation: 'fade',
            headerShown: false,
            contentStyle: {
              backgroundColor: 'transparent',
            },
          }}
        />
        <Stack.Screen
          name="create-company"
          options={{
            presentation: 'transparentModal',
            animation: 'fade',
            headerShown: false,
            contentStyle: {
              backgroundColor: 'transparent',
            },
          }}
        />
        <Stack.Screen name="auto-apply" />
        {/* The owned link for an agent artifact; the viewer is a dialog. */}
        <Stack.Screen name="artifacts" />
        {/* Native opens job preferences from here so the sheet presents over
            Jobs instead of the CV scene; web keeps linking to the CV route.
            Declared anyway so an undeclared route cannot sit outside every
            guard and render the editor shell to a signed-out visitor. */}
        <Stack.Screen name="job-preferences" />
        {/* Live mode: signed-in only, like every agent surface. */}
        <Stack.Screen name="voice-handsfree" />
      </Stack.Protected>
    </Stack>
  );

  const content = shouldLoadProtectedRuntime ? (
    <Suspense
      fallback={
        isResumeImport ? <ResumeImportStartupShell /> : <StartupShell />
      }
    >
      <ProtectedAppProviders>
        {shouldShowProtectedShell ? (
          <ProtectedWebShell>{navigator}</ProtectedWebShell>
        ) : (
          navigator
        )}
      </ProtectedAppProviders>
    </Suspense>
  ) : (
    navigator
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <WebFontLinks />
      <WebPageMetadata />
      <StatusBar style="auto" />
      <WebSentryInitializer />
      <Toaster position="top-right" richColors closeButton />
      <WebAlertHost />
      <WebObservabilityTracker />
      <DeferredWebAuthEffects />
      {content}
    </View>
  );
}

function RootWebLayout() {
  return (
    <Head.Provider>
      <QueryProvider>
        <ThemeProvider>
          <FontProvider>
            <AuthProvider>
              <PrivacyPreferencesProvider>
                <DialogProvider>
                  <DialogRegistrar />
                  <WebLayoutInner />
                  <PrivacyConsentSheet />
                  <ImpersonationBanner />
                </DialogProvider>
              </PrivacyPreferencesProvider>
            </AuthProvider>
          </FontProvider>
        </ThemeProvider>
      </QueryProvider>
    </Head.Provider>
  );
}

export { ErrorBoundary } from '../components/common/RootErrorBoundary';

export default wrapRootWithSentry(RootWebLayout);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  resumeImportFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  resumeImportFallbackCard: {
    width: '100%',
    maxWidth: 460,
    borderWidth: 1,
    borderRadius: 20,
  },
});
